import React, { useEffect, useMemo, useRef, useState } from 'react';
import { sendGRIRMessage, GRIR_QUICK_QUERIES } from '../../utils/grirChatApi';
import '../../Styles/GRIRFloatingChatWidget.css';

const GRIR_WIDGET_STORAGE_KEY = 'grir_widget_state_v1';

const WELCOME_MESSAGE = {
  id: 'welcome',
  sender: 'ai',
  text: 'Hi, I am your GR/IR Assistant. Ask me about matching, unmatched invoices, and reconciliation insights.',
};

function nowLabel() {
  return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function buildSessionId() {
  return `grir-widget-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function makeSessionTitle(question) {
  const clean = (question || '').trim();
  if (!clean) return 'New chat';
  return clean.length > 30 ? `${clean.slice(0, 30)}...` : clean;
}

function buildSession(title = 'New chat') {
  return {
    id: buildSessionId(),
    title,
    updatedAt: Date.now(),
    conversationHistory: [],
    messages: [WELCOME_MESSAGE],
  };
}

function getInitialState() {
  try {
    const raw = localStorage.getItem(GRIR_WIDGET_STORAGE_KEY);
    if (!raw) {
      const initialSession = buildSession('Chat 1');
      return {
        open: true,
        expanded: false,
        fullScreen: false,
        activeSessionId: initialSession.id,
        sessions: [initialSession],
      };
    }

    const parsed = JSON.parse(raw);
    const savedSessions = Array.isArray(parsed.sessions) && parsed.sessions.length > 0
      ? parsed.sessions
      : [buildSession('Chat 1')];

    const activeSessionId = savedSessions.some((session) => session.id === parsed.activeSessionId)
      ? parsed.activeSessionId
      : savedSessions[0].id;

    return {
      open: typeof parsed.open === 'boolean' ? parsed.open : true,
      expanded: typeof parsed.expanded === 'boolean' ? parsed.expanded : false,
      fullScreen: typeof parsed.fullScreen === 'boolean' ? parsed.fullScreen : false,
      activeSessionId,
      sessions: savedSessions,
    };
  } catch (_error) {
    const initialSession = buildSession('Chat 1');
    return {
      open: true,
      expanded: false,
      fullScreen: false,
      activeSessionId: initialSession.id,
      sessions: [initialSession],
    };
  }
}

const QUICK_PROMPTS = GRIR_QUICK_QUERIES.map((query) => query.prompt).slice(0, 4);

const GRIRChatWidget = () => {
  const initial = useMemo(() => getInitialState(), []);
  const [open, setOpen] = useState(initial.open);
  const [expanded, setExpanded] = useState(initial.expanded);
  const [fullScreen, setFullScreen] = useState(initial.fullScreen);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sessions, setSessions] = useState(initial.sessions);
  const [activeSessionId, setActiveSessionId] = useState(initial.activeSessionId);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const messagesEndRef = useRef(null);

  const activeSession = useMemo(
    () => sessions.find((session) => session.id === activeSessionId) || sessions[0],
    [sessions, activeSessionId]
  );

  const messages = activeSession?.messages || [WELCOME_MESSAGE];

  useEffect(() => {
    localStorage.setItem(
      GRIR_WIDGET_STORAGE_KEY,
      JSON.stringify({
        open,
        expanded,
        fullScreen,
        activeSessionId,
        sessions,
      })
    );
  }, [open, expanded, fullScreen, activeSessionId, sessions]);

  useEffect(() => {
    if (!fullScreen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setFullScreen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [fullScreen]);

  useEffect(() => {
    if (fullScreen) {
      document.body.classList.add('grir-widget-fullscreen-active');
    } else {
      document.body.classList.remove('grir-widget-fullscreen-active');
    }

    return () => {
      document.body.classList.remove('grir-widget-fullscreen-active');
    };
  }, [fullScreen]);

  useEffect(() => {
    if (open && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [open, messages, loading]);

  const canSend = useMemo(() => input.trim().length > 0 && !loading, [input, loading]);

  const updateActiveSession = (patch) => {
    setSessions((prev) => prev.map((session) => (
      session.id === activeSessionId
        ? { ...session, ...patch, updatedAt: Date.now() }
        : session
    )));
  };

  const sendMessage = async (question) => {
    const clean = (question || '').trim();
    if (!clean || loading) return;

    const userMessage = {
      id: `u-${Date.now()}`,
      sender: 'user',
      text: clean,
      time: nowLabel(),
    };

    const nextTitle = activeSession?.messages?.length <= 1 ? makeSessionTitle(clean) : activeSession?.title;
    const nextMessages = [...messages, userMessage];

    updateActiveSession({
      title: nextTitle,
      messages: nextMessages,
    });

    setInput('');
    setError('');
    setLoading(true);

    try {
      const result = await sendGRIRMessage(clean, activeSession?.conversationHistory || []);

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch chatbot response');
      }

      const aiMessage = {
        id: `a-${Date.now()}`,
        sender: 'ai',
        text: result.response || 'No response returned by GRIR assistant.',
        time: nowLabel(),
      };

      updateActiveSession({
        messages: [...nextMessages, aiMessage],
        conversationHistory: result.conversation_history || [],
      });
    } catch (err) {
      setError(err.message || 'Failed to fetch chatbot response');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (canSend) {
      sendMessage(input);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (canSend) {
        sendMessage(input);
      }
    }
  };

  const handleNewChat = () => {
    const nextSession = buildSession(`Chat ${sessions.length + 1}`);
    setSessions((prev) => [nextSession, ...prev]);
    setActiveSessionId(nextSession.id);
    setInput('');
    setError('');
    setLoading(false);
  };

  const handleClearChat = () => {
    updateActiveSession({
      title: 'New chat',
      messages: [WELCOME_MESSAGE],
      conversationHistory: [],
    });
    setError('');
  };

  const handleStartRenameSession = (session) => {
    setEditingSessionId(session.id);
    setRenameValue(session.title || 'New chat');
  };

  const handleCancelRenameSession = () => {
    setEditingSessionId(null);
    setRenameValue('');
  };

  const handleSaveRenameSession = (sessionId) => {
    const nextTitle = (renameValue || '').trim();
    if (!nextTitle) return;

    setSessions((prev) => prev.map((session) => (
      session.id === sessionId
        ? { ...session, title: nextTitle, updatedAt: Date.now() }
        : session
    )));

    handleCancelRenameSession();
  };

  const handleDeleteSession = (sessionId) => {
    setSessions((prev) => {
      const remaining = prev.filter((session) => session.id !== sessionId);

      if (remaining.length === 0) {
        const fallback = buildSession('Chat 1');
        setActiveSessionId(fallback.id);
        return [fallback];
      }

      if (activeSessionId === sessionId) {
        setActiveSessionId(remaining[0].id);
      }

      return remaining;
    });

    if (editingSessionId === sessionId) {
      handleCancelRenameSession();
    }
  };

  const sortedSessions = useMemo(
    () => [...sessions].sort((a, b) => Number(b.updatedAt || 0) - Number(a.updatedAt || 0)),
    [sessions]
  );

  return (
    <div className={`grir-widget-container ${open ? 'is-open' : ''} ${fullScreen ? 'fullscreen' : ''}`}>
      {!open && (
        <button
          type="button"
          className="grir-widget-toggle"
          onClick={() => setOpen(true)}
          aria-label="Open GRIR chat assistant"
        >
          Ask GRIR
        </button>
      )}

      {open && (
        <section
          className={`grir-widget-panel ${expanded ? 'expanded' : ''}`}
          aria-label="GRIR Chat Widget"
        >
          <header className="grir-widget-header">
            <div className="grir-widget-title">
              <h3>GR/IR Assistant</h3>
              <p>{loading ? 'Analyzing...' : 'Live API answers'}</p>
            </div>
            <div className="grir-widget-actions">
              <button type="button" onClick={handleNewChat}>New</button>
              <button type="button" onClick={handleClearChat}>Clear</button>
              <button type="button" onClick={() => setExpanded((prev) => !prev)}>{expanded ? 'Min' : 'Max'}</button>
              {fullScreen ? (
                <button type="button" onClick={() => setFullScreen(false)}>Exit</button>
              ) : (
                <button type="button" onClick={() => { setExpanded(true); setFullScreen(true); }}>Full</button>
              )}
              <button type="button" onClick={() => { setOpen(false); setFullScreen(false); }}>Close</button>
            </div>
          </header>

          <div className="grir-widget-body">
            {expanded && (
              <aside className="grir-widget-sessions" aria-label="Chat sessions">
                <h4>Sessions</h4>
                <div className="grir-widget-session-list">
                  {sortedSessions.map((session) => (
                    <div
                      key={session.id}
                      className={`grir-widget-session-item ${session.id === activeSessionId ? 'active' : ''}`}
                    >
                      {editingSessionId === session.id ? (
                        <div className="grir-widget-session-rename" onClick={(event) => event.stopPropagation()}>
                          <input
                            value={renameValue}
                            onChange={(event) => setRenameValue(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                handleSaveRenameSession(session.id);
                              }
                              if (event.key === 'Escape') {
                                event.preventDefault();
                                handleCancelRenameSession();
                              }
                            }}
                            autoFocus
                          />
                          <div className="grir-widget-session-rename-actions">
                            <button type="button" onClick={() => handleSaveRenameSession(session.id)}>Save</button>
                            <button type="button" onClick={handleCancelRenameSession}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="grir-widget-session-main"
                            onClick={() => {
                              setActiveSessionId(session.id);
                              setError('');
                            }}
                          >
                            <strong>{session.title || 'New chat'}</strong>
                            <span>{new Date(session.updatedAt || Date.now()).toLocaleString([], { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                          </button>
                          <div className="grir-widget-session-item-actions">
                            <button type="button" onClick={() => handleStartRenameSession(session)}>Edit</button>
                            <button type="button" onClick={() => handleDeleteSession(session.id)}>Del</button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </aside>
            )}

            <div className="grir-widget-main">
              <div className="grir-widget-messages">
                {messages.length <= 1 && (
                  <div className="grir-widget-quick-prompts">
                    {QUICK_PROMPTS.map((prompt) => (
                      <button key={prompt} type="button" onClick={() => sendMessage(prompt)}>{prompt}</button>
                    ))}
                  </div>
                )}

                {messages.map((message) => (
                  <article key={message.id} className={`grir-widget-message ${message.sender === 'user' ? 'user' : 'ai'}`}>
                    <div className="grir-widget-bubble">
                      <p>{message.text}</p>
                      <small className="grir-widget-time">{message.time || ''}</small>
                    </div>
                  </article>
                ))}

                {loading && (
                  <div className="grir-widget-typing">
                    <span />
                    <span />
                    <span />
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {error && <p className="grir-widget-error">{error}</p>}

              <form className="grir-widget-input" onSubmit={handleSubmit}>
                <textarea
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={2}
                  placeholder="Ask about unmatched invoices, reconciliation and SAP sync..."
                />
                <button type="submit" disabled={!canSend}>{loading ? 'Sending...' : 'Send'}</button>
              </form>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default GRIRChatWidget;
